import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuth } from '../stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable without an account — the share flow and the error screens. */
    public?: boolean
    /** Rendered bare, with no app shell around it. */
    bare?: boolean
  }
}

/**
 * These must stay in step with `server/spa.ts`, which decides which paths get
 * the app shell instead of the preview proxy. Two short lists that have to
 * agree, rather than one wildcard that would swallow mistyped preview URLs and
 * answer them with the login screen.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { public: true, bare: true },
  },
  {
    // The recipient of a shared link: no account, one code.
    path: '/share/:uuid',
    name: 'share',
    component: () => import('../views/ShareView.vue'),
    meta: { public: true, bare: true },
  },
  {
    path: '/error/:kind',
    name: 'error',
    component: () => import('../views/ErrorView.vue'),
    meta: { public: true, bare: true },
  },
  {
    path: '/',
    component: () => import('../layouts/AppLayout.vue'),
    children: [
      // The picker IS the home screen. It deliberately does not redirect into
      // the last preview: that made the picker unreachable without clearing
      // the session, so switching site meant signing out.
      { path: '', name: 'pick', component: () => import('../views/PickerView.vue') },
      { path: 'pick', redirect: { name: 'pick' } },
      { path: 'recents', name: 'recents', component: () => import('../views/RecentsView.vue') },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

/**
 * Holds the first navigation until the session is known.
 *
 * Without the wait, the guard runs before `/api/auth/me` has answered, reads
 * "not signed in" and bounces a signed-in user to the login screen on every
 * cold load.
 */
router.beforeEach(async (to) => {
  const auth = useAuth()
  if (!auth.ready.value) await auth.load()

  if (to.meta.public === true) return true
  if (auth.signedIn.value) return true

  // `redirect` so signing in returns to what was asked for, rather than
  // always landing on the picker.
  return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
})
